import { Button } from '@/components/ui/button';
import {
  findProfileForRole,
  getProfileDashboardPath,
  getRoleDashboardPath,
  profileToRole,
} from '@/lib/authRouting';
import { useAuthStore } from '@/stores/authStore';
import type { UserProfile, UserRole } from '@ergshoot/shared';
import { Check, Crown, Dumbbell, Shield, Store, User, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface RoleOption {
  role: UserRole;
  label: string;
  icon: React.ReactNode;
  description: string;
  profiles: UserProfile[];
}

const ROLE_META: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  hq_admin: {
    label: 'HQ Admin',
    icon: <Shield className="h-6 w-6" />,
    description: 'Full platform management',
  },
  agent: {
    label: 'Agent',
    icon: <Crown className="h-6 w-6" />,
    description: 'Territory & affiliate management',
  },
  affiliate_owner: {
    label: 'Affiliate Owner',
    icon: <Store className="h-6 w-6" />,
    description: 'Gym management & programming',
  },
  affiliate_coach: {
    label: 'Coach',
    icon: <Users className="h-6 w-6" />,
    description: 'Team coaching & programming',
  },
  member: {
    label: 'Member',
    icon: <User className="h-6 w-6" />,
    description: 'Gym workouts & leaderboards',
  },
  individual: {
    label: 'Individual',
    icon: <Dumbbell className="h-6 w-6" />,
    description: 'Solo training & global leaderboards',
  },
};

export default function ProfileSelection() {
  const navigate = useNavigate();
  const profiles = useAuthStore((s) => s.profiles);
  const user = useAuthStore((s) => s.user);
  const setSessionActiveRoles = useAuthStore((s) => s.setSessionActiveRoles);
  const switchProfile = useAuthStore((s) => s.switchProfile);
  const [isContinuing, setIsContinuing] = useState(false);

  // Build role options from profiles
  const roleOptions = useMemo((): RoleOption[] => {
    const roleMap = new Map<UserRole, UserProfile[]>();

    // Add primary role
    if (user?.role) {
      const meta = ROLE_META[user.role];
      if (meta) {
        roleMap.set(user.role, []);
      }
    }

    // Add roles from profiles
    for (const profile of profiles) {
      if (profile.is_active === false) continue;
      const role = profileToRole(profile);
      const existing = roleMap.get(role) || [];
      existing.push(profile);
      roleMap.set(role, existing);
    }

    return Array.from(roleMap.entries()).map(([role, profs]) => {
      const meta = ROLE_META[role] || {
        label: role,
        icon: <User className="h-6 w-6" />,
        description: '',
      };
      return { role, profiles: profs, ...meta };
    });
  }, [profiles, user?.role]);

  // Selection state
  const [selectedRoles, setSelectedRoles] = useState<Set<UserRole>>(
    new Set(roleOptions.map((r) => r.role))
  );
  const allSelected = selectedRoles.size === roleOptions.length;

  const toggleAll = useCallback(() => {
    if (allSelected) {
      // Deselect all (but keep at least one)
      setSelectedRoles(new Set());
    } else {
      setSelectedRoles(new Set(roleOptions.map((r) => r.role)));
    }
  }, [allSelected, roleOptions]);

  const toggleRole = useCallback((role: UserRole) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  }, []);

  const handleContinue = useCallback(async () => {
    if (selectedRoles.size === 0) return;

    const roles = selectedRoles.size === roleOptions.length ? null : Array.from(selectedRoles);

    setIsContinuing(true);
    try {
      let targetProfile: UserProfile | null = null;

      // Navigate to the most appropriate dashboard
      if (roles === null || roles.length > 1) {
        // ALL or multiple — go to primary profile's dashboard
        targetProfile =
          profiles.find((p) => p.is_primary && p.is_active !== false) || profiles[0] || null;
        if (targetProfile) {
          await switchProfile(targetProfile.id);
        }
        setSessionActiveRoles(roles);
        if (targetProfile) {
          navigate(getProfileDashboardPath(targetProfile), { replace: true });
        } else {
          navigate('/admin', { replace: true });
        }
      } else {
        // Single role selected — activate that profile before entering its portal
        const singleRole = roles[0] as UserRole;
        targetProfile = findProfileForRole(profiles, singleRole);
        if (targetProfile) {
          await switchProfile(targetProfile.id);
        }
        setSessionActiveRoles(roles);
        navigate(getRoleDashboardPath(singleRole), { replace: true });
      }
    } finally {
      setIsContinuing(false);
    }
  }, [navigate, profiles, roleOptions.length, selectedRoles, setSessionActiveRoles, switchProfile]);

  // If only 1 profile, skip this screen
  useEffect(() => {
    if (roleOptions.length === 0) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (roleOptions.length !== 1) return;

    const role = roleOptions[0]?.role;
    navigate(role ? getRoleDashboardPath(role) : '/', { replace: true });
  }, [roleOptions, navigate]);

  if (roleOptions.length <= 1) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground">
            Welcome back, @{user?.username || 'athlete'}
          </h1>
          <p className="text-muted-foreground mt-2">
            Select which roles you'd like to use this session
          </p>
        </div>

        {/* ALL toggle */}
        <button
          type="button"
          onClick={toggleAll}
          className={`w-full mb-4 flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
            allSelected
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-card text-muted-foreground hover:border-primary/50'
          }`}
        >
          <span className="font-semibold text-lg">ALL ROLES</span>
          {allSelected && <Check className="h-5 w-5" />}
        </button>

        {/* Individual role cards */}
        <div className="grid gap-3 sm:grid-cols-2">
          {roleOptions.map((option) => {
            const isSelected = selectedRoles.has(option.role);
            return (
              <button
                key={option.role}
                type="button"
                onClick={() => toggleRole(option.role)}
                className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:border-primary/50 opacity-60'
                }`}
              >
                <div className={`mt-0.5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-foreground">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{option.description}</p>
                  {option.profiles.length > 0 && option.profiles[0]?.context?.affiliate?.name && (
                    <p className="text-xs text-primary mt-1">
                      {option.profiles[0].context.affiliate.name}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Continue button */}
        <div className="mt-8">
          <Button
            onClick={handleContinue}
            disabled={selectedRoles.size === 0 || isContinuing}
            className="w-full"
            size="lg"
          >
            {isContinuing ? 'Switching...' : 'Continue'}
            {selectedRoles.size > 0 && selectedRoles.size < roleOptions.length
              ? ` as ${selectedRoles.size} role${selectedRoles.size > 1 ? 's' : ''}`
              : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
